import { loadStripe } from "@stripe/stripe-js";
import { stripePublishableKey } from "utils/stripePublishableKey";
import { toast } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import AccountType from "enums/AccountType";
import useCompleteBankLink, { CompletedBankLink } from "./useCompleteBankLink";

const useAcceptTerms = (
  accountKey: string,
  clientSecret: string,
  /** Called once the link settles, and again if a later Retry saves the bank. */
  handleCloseAddBank: (refresh: boolean, linkedAccountId?: string) => void,
  type: AccountType,
  publishableKey?: string | null,
  /** The program the bank is linked for; without one the company default issuer is used. */
  programId?: string,
) => {
  const { completeBankLink, completing: savingHostedBankAccount } =
    useCompleteBankLink();
  const { t } = useTranslation();

  const settle = (link: CompletedBankLink) => {
    toast.success("Account successfully added!");
    return link.accounts[0]?.id;
  };

  /** Resolves to the id of the bank the link saved, if it saved one. */
  async function handleAcceptTerms(): Promise<string | undefined> {
    let linkedAccountId: string | undefined;
    try {
      const stripe = await loadStripe(stripePublishableKey(publishableKey), {
        stripeAccount: accountKey,
      });
      const response = await stripe?.confirmUsBankAccountSetup(clientSecret);
      if (response?.setupIntent?.status === "requires_payment_method") {
        toast.error(
          "Confirmation failed. Please try again to complete account setup.",
        );
      } else if (
        response?.setupIntent?.status === "succeeded" ||
        response?.setupIntent?.next_action?.type === "verify_with_microdeposits"
      ) {
        const paymentMethod = response.setupIntent.payment_method;
        const link = await completeBankLink(
          {
            accountId:
              typeof paymentMethod === "string"
                ? paymentMethod
                : paymentMethod?.id,
            type,
            ...(programId && { programId }),
          },
          (retried) => handleCloseAddBank(true, settle(retried)),
        );
        if (link) linkedAccountId = settle(link);
      }
      if (response?.error) {
        toast.error(t("SomethingWentWrongDescriptionWithSupport"));
      }
    } catch (error: unknown) {
      toast.error(t("SomethingWentWrongDescriptionWithSupport"));
    }
    handleCloseAddBank(true, linkedAccountId);
    return linkedAccountId;
  }
  return { handleAcceptTerms, savingHostedBankAccount };
};

export default useAcceptTerms;
